import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/shared/lib/utils"

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-lg border border-transparent bg-clip-padding whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border border-amber-400/50 bg-amber-500 text-black shadow-[0_4px_16px_rgba(245,158,11,0.25)] hover:bg-amber-400 hover:border-amber-300 hover:shadow-[0_6px_20px_rgba(245,158,11,0.35)]",
        amber:
          "border border-amber-400/50 bg-amber-500 text-black shadow-[0_4px_16px_rgba(245,158,11,0.25)] hover:bg-amber-400 hover:border-amber-300 hover:shadow-[0_6px_20px_rgba(245,158,11,0.35)]",
        indigo:
          "bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-[0_4px_16px_rgba(99,102,241,0.32)] hover:from-indigo-400 hover:to-violet-400 hover:shadow-[0_6px_22px_rgba(99,102,241,0.42)] border-transparent",
        outline:
          "border-border bg-background/40 hover:bg-accent/50 hover:text-accent-foreground aria-expanded:bg-accent aria-expanded:text-accent-foreground dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[color-mix(in_oklch,var(--secondary),var(--foreground)_5%)] aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground dark:hover:bg-muted/50",
        destructive:
          "border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:hover:bg-destructive/30",
        destructiveSolid:
          "bg-gradient-to-r from-rose-600 to-red-600 text-white shadow-[0_4px_16px_rgba(225,29,72,0.28)] hover:from-rose-500 hover:to-red-500 border-transparent",
        accent:
          "border border-indigo-500/35 bg-indigo-500/10 text-indigo-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] hover:border-indigo-400/50 hover:bg-indigo-500/16 hover:text-indigo-100",
        teal: "border-transparent bg-teal-600 text-white shadow-[0_0_24px_-6px_rgba(45,212,191,0.35)] hover:bg-teal-500",
        link: "text-primary underline-offset-4 hover:underline",
        /** 顶栏「登录/注册」— 与 portal 视觉一致 */
        portal:
          "hidden min-h-9 rounded-full border border-violet-400/85 bg-gradient-to-b from-[#2a1455]/95 via-[#6d28d9]/88 to-[#6d28d9]/82 px-4 font-semibold text-white shadow-[0_0_12px_rgba(139,92,246,0.32),inset_0_1px_0_rgba(255,255,255,0.15)] hover:brightness-110 xl:inline-flex",
        /** 顶栏「会员中心」 */
        member:
          "hidden min-h-9 gap-1.5 rounded-full border border-amber-400/40 bg-gradient-to-r from-[#3d3428]/75 to-[#2c2721]/80 px-4 font-semibold text-amber-300 shadow-none hover:border-amber-400/55 hover:text-amber-200 xl:inline-flex data-[active=true]:border-amber-400/55 data-[active=true]:text-amber-300 data-[active=true]:shadow-[0_0_12px_rgba(248,182,45,0.28)]",
      },
      size: {
        default:
          "h-8 gap-1.5 px-2.5 text-sm font-medium has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs font-medium in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-xs font-medium in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-9 gap-1.5 px-2.5 text-sm font-medium has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2",
        icon: "size-8",
        "icon-xs":
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
